import { SyntheticEvent, useState } from 'react';
import { ReferenceManyField, ShowBase, useShowContext } from 'react-admin';
import ImageGallery from 'react-image-gallery';
import { matchPath, useLocation } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import {
	Box,
	Card,
	CardContent,
	Dialog,
	DialogContent,
	Divider,
	IconButton,
	Stack,
	Tab,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableRow,
	Tabs,
	Typography
} from '@mui/material';
import { findIndex } from 'lodash';
import { ImagesGalleryModal } from '../../../../misc/ImagesGalleryModal';
import { TabContent } from '../../../../misc/TabContent';
import { NotesIterator } from '../../../../notes';
import { GoToListButton } from '../../../components';
import { OBJECTS_TYPES } from '../../../constants';
import { ApartmentAside } from '../ApartmentAside';
import { GeneralInfo } from './GeneralInfo';
import { TechnicalInfo } from './TechnicalInfo';

import styles from './index.module.css';

export const ApartmentShow = () => {
	const { pathname } = useLocation();
	const match = matchPath(
		{ path: `objects/${OBJECTS_TYPES.APARTMENTS}/show/:id` },
		pathname
	);

	if (!match) {
		return null;
	}

	const { params } = match;

	return (
		<Box mt={3}>
			<GoToListButton />
			<ShowBase id={params.id} resource={OBJECTS_TYPES.PROPERTIES}>
				<ApartmentShowContent />
			</ShowBase>
		</Box>
	);
};

const ApartmentShowContent = () => {
	const { record, isPending } = useShowContext();
	const [tabIndex, setTabIndex] = useState(0);

	if (!record) return null;

	const {
		rent_usd_object_price,
		sale_usd_object_price,
		layout,
		video,
		name,
		description
	} = record;
	// const translate = useTranslate();

	const handleTabChange = (event: SyntheticEvent, newValue: number) => {
		setTabIndex(newValue);
	};

	if (isPending || !record) return null;

	return (
		<Box mt={4} mb={2} display="flex">
			<Box flex="1">
				<Card>
					<CardContent
						sx={{
							p: 0
						}}
					>
						<Stack>
							<Box display="flex" gap={1} sx={{ p: 2, flex: '1 1 50%' }}>
								{/* Image Gallery */}
								<ImageList images={record.image} />
								<Stack gap={1}>
									{/* Layout Image */}
									<LayoutImage layout={layout} />

									{/* Videos */}
									<VideoList videos={video} />
								</Stack>

								{/* Documents */}
								{/*<DocumentList documents={record.document} />*/}
							</Box>
							<Divider />
							<Typography sx={{ px: 2, py: 1 }} variant="h6">
								{name}
							</Typography>
							<Divider />
						</Stack>
						<Stack>
							<Tabs
								sx={{
									display: 'flex',
									borderBottom: 1,
									borderColor: 'divider'
								}}
								value={tabIndex}
								onChange={handleTabChange}
								textColor="inherit"
								variant="fullWidth"
							>
								<Tab label="Інформація" />
								<Tab label="Технічні деталі" />
								<Tab label="Ціни" />
								<Tab label="Документи" />
								<Tab label="Контакти" />
							</Tabs>
							<Stack gap={2}>
								<TabContent value={tabIndex} index={0}>
									<GeneralInfo />
								</TabContent>
								<TabContent value={tabIndex} index={1}>
									<TechnicalInfo />
								</TabContent>
								<TabContent value={tabIndex} index={2}>
									<TableContainer>
										<Table size="small">
											<TableBody>
												{[
													{
														title: 'Оренда',
														value: `${rent_usd_object_price} $`,
														isDisplay: Boolean(rent_usd_object_price)
													},
													{
														title: 'Продаж',
														value: `${sale_usd_object_price} $`,
														isDisplay: Boolean(rent_usd_object_price)
													}
												].map(
													({ title, value, isDisplay }, index) =>
														isDisplay && (
															<TableRow key={index}>
																<TableCell width={280}>{title}</TableCell>
																<TableCell sx={{ fontWeight: 'medium' }}>
																	{value}
																</TableCell>
															</TableRow>
														)
												)}
											</TableBody>
										</Table>
									</TableContainer>
								</TabContent>
								<TabContent value={tabIndex} index={3}>
									Документи
								</TabContent>
								<TabContent value={tabIndex} index={4}>
									Контакти
								</TabContent>
							</Stack>
						</Stack>
						<Box px={2}>
							<div dangerouslySetInnerHTML={{ __html: description }} />
						</Box>
						<Divider />
						<Box p={2}>
							<ReferenceManyField
								target="object_id"
								reference="objectNotes"
								sort={{ field: 'date', order: 'DESC' }}
							>
								<NotesIterator showDate={false} reference="objects" />
							</ReferenceManyField>
						</Box>
					</CardContent>
				</Card>
			</Box>
			<ApartmentAside link="edit" />
		</Box>
	);
};
const ImageList = ({ images }: any) => {
	console.log('image', images);
	console.log('image', images);
	const [isOpen, setIsOpen] = useState(false);
	const [imageIndex, setImageIndex] = useState(0);

	if (!images || images.length === 0) {
		return <Typography>No images available</Typography>;
	}

	const parsedImages =
		images?.map(({ src }: { src: string }) => ({
			original: src,
			thumbnail: src,
			originalClass: styles.image
		})) ?? [];

	const handleClick = (imgSrc: string) => {
		setImageIndex(findIndex(parsedImages, ['original', imgSrc]));
		setIsOpen(true);
	};

	const renderItem = (item: any) => (
		<img
			src={item.original}
			alt={item.original}
			onClick={() => handleClick(item.original)}
			style={{
				width: '100%',
				cursor: 'pointer',
				maxWidth: 600,
				minWidth: 600,
				height: 400,
				objectFit: 'contain'
			}}
		/>
	);

	return (
		<Box display="flex" flexWrap="wrap" gap={1}>
			<ImageGallery
				items={parsedImages}
				showPlayButton={false}
				renderItem={renderItem}
				showFullscreenButton={false}
				additionalClass={styles.imagesContainer}
			/>
			<ImagesGalleryModal
				startIndex={imageIndex}
				items={parsedImages}
				isOpen={isOpen}
				onClose={() => setIsOpen(false)}
			/>
		</Box>
	);
};
const VideoList = ({ videos }: any) => {
	if (!videos || videos.length === 0) {
		return <Typography>No videos available</Typography>;
	}
	return (
		<Box display="flex" flexWrap="wrap" gap={2} mt={2}>
			{videos.map((video: any, index: number) => (
				<video key={index} width="200" height="200" controls>
					<source src={video?.src} />
					Your browser does not support the video tag.
				</video>
			))}
		</Box>
	);
};
const DocumentList = ({ documents }: any) => {
	if (!documents || documents.length === 0) {
		return <Typography>No documents available</Typography>;
	}
	return (
		<Box>
			<Typography variant="h6" gutterBottom>
				Документи
			</Typography>
			<ul style={{ paddingLeft: '1.5rem' }}>
				{documents.map((doc: any, index: number) => (
					<li key={index}>
						<a href={doc.src} target="_blank" rel="noopener noreferrer">
							{doc.title || `Document ${index + 1}`}
						</a>
					</li>
				))}
			</ul>
		</Box>
	);
};
const LayoutImage = ({ layout }: any) => {
	const [open, setOpen] = useState(false);

	const handleClick = () => setOpen(true);
	const handleClose = () => setOpen(false);

	if (!layout || !layout.src) {
		return <Typography>No layout image available</Typography>;
	}

	return (
		<Box>
			<>
				<Box
					component="img"
					src={layout.src}
					alt="thumbnail"
					sx={{
						width: 200,
						height: 200,
						objectFit: 'cover',
						borderRadius: 1,
						cursor: 'pointer'
					}}
					onClick={handleClick}
				/>

				<Dialog open={open} onClose={handleClose} maxWidth="md">
					<IconButton
						onClick={handleClose}
						sx={{
							position: 'absolute',
							top: 8,
							right: 8,
							color: 'grey.500',
							zIndex: 1
						}}
					>
						<CloseIcon />
					</IconButton>
					<DialogContent>
						<Box
							component="img"
							src={layout.src}
							alt="full image"
							sx={{ width: '100%', height: 'auto', maxWidth: 600 }}
						/>
					</DialogContent>
				</Dialog>
			</>
		</Box>
	);
};
